import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Search } from "lucide-react";
import { SkyButton, SkyInput } from "@skypointcloud/skypoint-ui";
import { useNativeInputAttrs } from "@/hooks/useNativeInputAttrs";
import { ROUTES } from "@/components/shared/constant";
import { employeeViewOf, toViewPath } from "@/routes/employeePaths";
import { useMsalAuth } from "@/hooks/useMsalAuth";
import { useEmployeeChrome } from "@/hooks/useEmployeeChrome";
import { useEmployeeAccess } from "@/views/employee/access/useEmployeeAccess";
import { useSearchEmployeeQuery } from "@/store/api/employeeApi";
import { SEARCH_MAX_LENGTH, SEARCH_MIN_LENGTH } from "../../../../shared/employee/search";
import { EmployeeAvatar } from "../ui/EmployeeAvatar";
import { POST_TYPE_LABELS, postPath } from "../feed/feedLabels";
import type { EmpPostType } from "../../../../shared/employee/feed";
import { RESOURCE_FILE_LABELS } from "../../../../shared/employee/resources";

const DEBOUNCE_MS = 250;

/**
 * "Search people, posts and resources" in the header, in skyEmployee
 * (decision P3-2) — its own pages and a shared page opened from it, like
 * Users & access. Results follow each page's own rules on the server.
 */
export function HeaderSearch() {
  // Outside skyEmployee nothing is asked of skyEmployee at all.
  if (!useEmployeeChrome()) return null;
  return <EmployeeHeaderSearch />;
}

function EmployeeHeaderSearch() {
  const access = useEmployeeAccess();
  return access.me ? <SearchPopover /> : null;
}

function SearchPopover() {
  const [open, setOpen] = useState(false);
  return (
    <PopoverPrimitive.Root
      open={open}
      onOpenChange={setOpen}
    >
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          aria-label="Search"
          className="flex h-9 items-center gap-2 rounded-full px-2.5 text-sm text-white/90 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          <Search
            className="h-5 w-5"
            aria-hidden="true"
          />
          <span className="hidden lg:inline">Search</span>
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="end"
          sideOffset={8}
          className="z-[1050] w-[min(420px,calc(100vw-24px))] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900"
        >
          {open && <SearchPanel onClose={() => setOpen(false)} />}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

function SearchPanel({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  // On a shared page (Users & access) links open in the admin view it's reached from.
  const pathname = employeeViewOf(useLocation().pathname);
  const viewPath = (path: string) => toViewPath(path, pathname);
  const { getQueryString } = useMsalAuth();
  const [input, setInput] = useState("");
  const searchRef = useNativeInputAttrs<HTMLInputElement>({ autoFocus: true });
  const [q, setQ] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setQ(input.trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [input]);

  const ready = q.length >= SEARCH_MIN_LENGTH;
  const { data, isFetching, isError, refetch } = useSearchEmployeeQuery(q, { skip: !ready });

  const openDirectory = (extra: Record<string, string>) => {
    const params = new URLSearchParams(getQueryString());
    for (const [k, v] of Object.entries(extra)) params.set(k, v);
    onClose();
    navigate(`${viewPath(ROUTES.EMPLOYEE_MY_DIRECTORY)}?${params.toString()}`);
  };

  const people = data?.people ?? [];
  const posts = data?.posts ?? [];
  const resources = data?.resources ?? [];
  const openResource = (id: string) => {
    onClose();
    const query = getQueryString();
    navigate(
      `${viewPath(ROUTES.EMPLOYEE_MY_RESOURCES)}${query ? `${query}&` : "?"}resource=${encodeURIComponent(id)}`,
    );
  };
  const openPost = (postId: string) => {
    onClose();
    navigate(viewPath(postPath(postId, getQueryString())));
  };
  const nothing =
    ready &&
    !isFetching &&
    !isError &&
    data &&
    people.length === 0 &&
    posts.length === 0 &&
    resources.length === 0;

  return (
    <div className="flex max-h-[min(520px,70vh)] flex-col">
      <div className="border-b border-gray-100 p-3 dark:border-gray-700">
        <label
          htmlFor="employee-search"
          className="sr-only"
        >
          Search people, posts and resources
        </label>
        {/* The label stays sr-only and outside, rather than becoming the
            `label` prop, which would render it visibly in the header. SkyInput
            takes an `id`, so the association still holds. */}
        <SkyInput
          ref={searchRef}
          id="employee-search"
          type="search"
          value={input}
          maxLength={SEARCH_MAX_LENGTH}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Search people, posts and resources"
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {!ready ? (
          <p className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
            Type at least {SEARCH_MIN_LENGTH} characters.
          </p>
        ) : isError ? (
          <div className="flex flex-col items-center gap-2 px-4 py-6 text-center">
            <p className="text-sm text-gray-600 dark:text-gray-300">Search couldn't load.</p>
            <SkyButton
              appearance="secondary"
              size="small"
              onClick={() => refetch()}
            >
              Try again
            </SkyButton>
          </div>
        ) : nothing ? (
          <p className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
            No people, posts or resources match "{q}".
          </p>
        ) : people.length === 0 && posts.length === 0 && resources.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-gray-500">Searching…</p>
        ) : (
          <>
            {people.length > 0 && (
              <section aria-label="People">
                <h3 className="px-4 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  People
                </h3>
                <ul>
                  {people.map((p) => (
                    <li key={p.partyId}>
                      <button
                        type="button"
                        onClick={() => openDirectory({ person: p.partyId })}
                        className="flex w-full items-center gap-3 px-4 py-2 text-left hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none dark:hover:bg-gray-800 dark:focus-visible:bg-gray-800"
                      >
                        <EmployeeAvatar
                          name={p.name}
                          photoUrl={p.photoUrl}
                          size={32}
                        />
                        <span className="min-w-0">
                          <span className="block truncate text-sm text-gray-900 dark:text-gray-100">
                            {p.name}
                          </span>
                          <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                            {[p.jobTitle, p.location?.name].filter(Boolean).join(" · ")}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
                <div className="px-4 pb-3 pt-1">
                  <SkyButton
                    appearance="transparent"
                    size="small"
                    onClick={() => openDirectory({ q })}
                  >
                    See all matching people
                  </SkyButton>
                </div>
              </section>
            )}
            {posts.length > 0 && (
              <section aria-label="Posts">
                <h3 className="px-4 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Posts
                </h3>
                <ul className="pb-2">
                  {posts.map((p) => (
                    <li key={p.id}>
                      <button
                        type="button"
                        onClick={() => openPost(p.id)}
                        className="flex w-full flex-col px-4 py-2 text-left hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none dark:hover:bg-gray-800 dark:focus-visible:bg-gray-800"
                      >
                        <span className="block truncate text-sm text-gray-900 dark:text-gray-100">
                          {p.title || p.snippet}
                        </span>
                        <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                          {[POST_TYPE_LABELS[p.type as EmpPostType] ?? "Post", p.authorName].join(
                            " · ",
                          )}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {resources.length > 0 && (
              <section aria-label="Resources">
                <h3 className="px-4 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Resources
                </h3>
                <ul className="pb-2">
                  {resources.map((r) => (
                    <li key={r.id}>
                      <button
                        type="button"
                        onClick={() => openResource(r.id)}
                        className="flex w-full flex-col px-4 py-2 text-left hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none dark:hover:bg-gray-800 dark:focus-visible:bg-gray-800"
                      >
                        <span className="block truncate text-sm text-gray-900 dark:text-gray-100">
                          {r.title}
                        </span>
                        <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                          {[
                            r.kind === "link"
                              ? "Link"
                              : r.kind === "video"
                                ? "Video"
                                : r.fileType
                                  ? RESOURCE_FILE_LABELS[r.fileType]
                                  : "File",
                            r.categoryName,
                            `updated ${r.versionPublishedAt.slice(0, 10)}`,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
